import React, { useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  useDeclineTriggerRun,
  useMyTriggerRuns,
} from "@/hooks/process-trigger/use-process-trigger";
import type { TriggerRun } from "@/types/process-trigger.types";

interface Props {
  // Compact mode hides the section entirely when there is nothing to do.
  compact?: boolean;
}

const TodoList: React.FC<Props> = ({ compact }) => {
  const { t } = useTranslation();
  const { data: runs = [], isLoading } = useMyTriggerRuns();
  const declineMutation = useDeclineTriggerRun();
  const [declining, setDeclining] = useState<TriggerRun | null>(null);
  const [reason, setReason] = useState("");

  if (compact && (isLoading || runs.length === 0)) return null;

  const handleDecline = () => {
    if (!declining) return;
    declineMutation.mutate(
      { id: declining.id, reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(t("processTriggers.todo.declined"));
          setDeclining(null);
          setReason("");
        },
        onError: () => toast.error(t("processTriggers.todo.declineFailed")),
      }
    );
  };

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-lg font-medium text-darkBlue">
        <ClipboardList className="h-5 w-5" /> {t("processTriggers.todo.title")}
        {runs.length > 0 && (
          <span className="rounded-full bg-amber-100 px-2 text-sm text-amber-800">{runs.length}</span>
        )}
      </h2>

      {isLoading ? (
        <p className="text-sm text-gray-500">{t("processManagement.loading")}</p>
      ) : runs.length === 0 ? (
        <p className="text-sm text-gray-500">{t("processTriggers.todo.empty")}</p>
      ) : (
        <ul className="space-y-3">
          {runs.map((run) => {
            const dueSoon = run.dueAt && dayjs(run.dueAt).diff(dayjs(), "day") < 2;
            return (
              <li
                key={run.id}
                className="flex flex-col md:flex-row md:items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/40 p-4"
              >
                <div className="space-y-1 min-w-0">
                  <p className="font-medium text-gray-900">{run.targetProcess.name}</p>
                  <p className="text-sm text-gray-600">
                    {t("processTriggers.todo.startedAfter", {
                      process: run.parentApplicantProcess.process.name,
                    })}
                  </p>
                  {run.trigger?.message && (
                    <p className="text-sm text-gray-700 whitespace-pre-line">{run.trigger.message}</p>
                  )}
                  {run.dueAt && (
                    <p className={`text-xs ${dueSoon ? "text-red-600" : "text-gray-500"}`}>
                      {t("processTriggers.todo.dueOn", {
                        date: dayjs(run.dueAt).format("MMM D, YYYY"),
                      })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {run.trigger?.allowDecline && (
                    <button
                      type="button"
                      className="px-4 py-2 text-sm border rounded bg-white hover:bg-gray-50"
                      onClick={() => setDeclining(run)}
                    >
                      {t("processTriggers.todo.decline")}
                    </button>
                  )}
                  <Link to={`/to-do/${run.id}`} className="main-dark-button text-sm">
                    {t("processTriggers.todo.start")}
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={!!declining} onOpenChange={(open) => !open && setDeclining(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("processTriggers.todo.declineTitle")}</DialogTitle>
            <DialogDescription>
              {t("processTriggers.todo.declineDescription", {
                process: declining?.targetProcess.name,
              })}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            maxLength={500}
            value={reason}
            placeholder={t("processTriggers.todo.declineReason")}
            onChange={(e) => setReason(e.target.value)}
          />
          <DialogFooter className="gap-2">
            <button
              type="button"
              className="px-4 py-2 text-sm border rounded"
              onClick={() => setDeclining(null)}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="main-dark-button"
              disabled={declineMutation.isPending}
              onClick={handleDecline}
            >
              {t("processTriggers.todo.decline")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default TodoList;
